import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Skeleton
} from '@mui/material';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import { IMAGES, sidebarActions } from '../../utils/constants';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import _ from 'lodash';
import { setSidebarCollapsed } from '../../store/slices/permission';
import StorageService from '../../services/storageService';

const drawerWidth = 260;
const collapsedWidth = 70;

interface SidebarProps {
  activeMenu: string;
}

interface SidebarMenu {
  disabled: boolean;
  label: string;
  key: string;
  path?: string;
  icon: React.ReactNode;
  activeIcon: React.ReactNode;
}

const Sidebar: FunctionComponent<SidebarProps> = ({ activeMenu }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarMenus, setSidebarMenus] = useState<SidebarMenu[]>([]);
  const modulePermissions = useSelector(
    (state: RootState) => state.permission.modulePermissions
  );
  const sidebarCollapsed = useSelector(
    (state: RootState) => state.permission.sidebarCollapsed
  );

  const [isCollapsed, setIsCollapsed] = useState(sidebarCollapsed);
  const selectedExtranet = StorageService.getSelectedExtranet();

  useEffect(() => {
    if (!_.isEmpty(modulePermissions)) {
      
      const updatedSidebarActions = sidebarActions.map((item) => {
        let updatedItem = {
          ...item,
          disabled: !(
            modulePermissions[item.key as keyof typeof modulePermissions] === true
          ),
        };
        
        // Dynamically set Home path based on extranet selection
        if (item.key === 'overview') {
          if (selectedExtranet === 'Barksdale Admin') {
            updatedItem.path = '/barksdale-admin';
          } else {
            updatedItem.path = '/dashboard';
          }
        }
        
        return updatedItem;
      });
      
      setSidebarMenus(updatedSidebarActions);
      setIsLoading(false);
    }
  }, [modulePermissions, selectedExtranet]);

  const handleMenuClick = (path: string | undefined, key: string) => {
    if (!path) return;

    if (path.startsWith('http://') || path.startsWith('https://')) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(path);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isCollapsed ? collapsedWidth : drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isCollapsed ? collapsedWidth : drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#c8102e',
          color: '#fff',
          position: 'relative',
          overflowX: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: isCollapsed ? 'center' : 'flex-end',
          alignItems: 'center',
          height: 48,
          px: 1,
        }}
      >
        <IconButton
          onClick={() => {
            setIsCollapsed((prev) => {
              const newValue = !prev;
              dispatch(setSidebarCollapsed(newValue));
              return newValue;
            });
          }}
          sx={{ color: '#fff' }}
          size="small"
        >
          {isCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
        </IconButton>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        overflow: 'hidden'
      }}>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <List>
            {isLoading ? (
              <>
                {Array.from({ length: 5 }).map((_, index) => (
                  <ListItem key={index} sx={{ pb: 0, pt: 0 }}>
                    <ListItemButton disabled sx={{ pl: isCollapsed ? 1 : 2 }}>
                      <ListItemIcon>
                        <Skeleton
                          variant="circular"
                          width={24}
                          height={24}
                          sx={{ bgcolor: '#d1c4e9' }}
                        />
                      </ListItemIcon>
                      {!isCollapsed && (
                        <ListItemText
                          primary={
                            <Skeleton width="80%" sx={{ bgcolor: '#d1c4e9' }} />
                          }
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </>
            ) : (
              <>
                {sidebarMenus.map((menu) => {
                  if (!menu.disabled) {
                    return (
                      <ListItem key={menu.key} sx={{ pb: 0, pt: 0 }}>
                        <ListItemButton
                          onClick={() => handleMenuClick(menu.path, menu.key)}
                          selected={menu.key === activeMenu}
                          sx={{
                            pl: isCollapsed ? '6px' : 2,
                            '&.Mui-selected': {
                              backgroundColor: '#fff',
                              color: '#c8102e',
                              borderRadius: '10px',
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: '#f0f0f0',
                              borderRadius: '10px',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              color: menu.key === activeMenu ? '#c8102e' : '#fff',
                              minWidth: 0,
                              mr: isCollapsed ? 0 : 2,
                              justifyContent: 'center',
                            }}
                          >
                            {menu.key === activeMenu
                              ? menu.activeIcon
                              : menu.icon}
                          </ListItemIcon>
                          {!isCollapsed && (
                            <ListItemText primary={menu.label} />
                          )}
                        </ListItemButton>
                      </ListItem>
                    );
                  }
                  return null;
                })}
              </>
            )}
          </List>
        </Box>

        {/* Bottom section - New Configurator text + Product Configurator and Technical Issue - Only when expanded */}
        {!isCollapsed && !isLoading && (
          <Box
            sx={{
              width: '75%',
              margin: '1em auto 0 auto',
              px: 1,
              pb: 2,
              mt: 'auto',
              flexShrink: 0,
            }}
          >
            {/* New Configurator Text */}
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: '#fff', mb: 1, textAlign: 'center' }}
            >
              New Configurator!
            </Typography>
            <Typography variant="body1" mb={2} sx={{ color: '#fff', textAlign: 'center' }}>
              Click to Try it!
            </Typography>

            {/* Product Configurator Card */}
            <Card sx={{ mb: 2 }}>
              <CardActionArea
                onClick={() =>
                  window.open(
                    'https://www.barksdale.com/configurator',
                    '_self'
                  )
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                  <img
                    src={IMAGES.productConfigurator}
                    alt="Configurator"
                    width={50}
                  />
                  <CardContent sx={{ padding: '8px !important' }}>
                    <Typography
                      sx={{
                        color: '#d01e42',
                        fontWeight: 'bold',
                        paddingBottom: 0,
                      }}
                    >
                      Product Configurator
                    </Typography>
                  </CardContent>
                </Box>
              </CardActionArea>
            </Card>

            {/* Technical Issue Card */}
            <Card>
              <CardActionArea
                onClick={() =>
                  window.open('https://apps.barksdale.com/extranet/ChannelPartners/TechRequestWebForm/', '_self')
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                  <img
                    src={IMAGES.technicalIssue}
                    alt="Support"
                    width={50}
                  />
                  <CardContent sx={{ padding: '8px !important' }}>
                    <Typography
                      sx={{
                        color: '#d01e42',
                        fontWeight: 'bold',
                        fontSize: 13,
                      }}
                    >
                      Technical Issue?
                    </Typography>
                    <Typography fontSize={13} sx={{ color: '#000' }}>
                      For <b style={{ color: '#d01e42' }}>FASTEST</b> Support
                    </Typography>
                  </CardContent>
                </Box>
              </CardActionArea>
            </Card>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
